import React from 'react';
import { FiChevronRight } from 'react-icons/fi';
import * as translator from '../../../../../utils/translator';
import { careProfileAsset } from '../careProfileAssets';
import { fillWeight, WeightUnit } from '../../../../../utils/weightUnits';

interface GoalReachedCardProps {
	language: string;
	totalLostKg: number;
	currentKg: number;
	onSetNewGoal?: () => void;
	weightUnit?: WeightUnit;
}

const titleGradient = 'linear-gradient(198.29deg, #ffb909 0.8%, #e17200 94.83%)';
const boldGradient = 'linear-gradient(199.03deg, #ffb909 0.8%, #e17200 94.83%)';

const t = (language: string, key: string, fallback: string): string => {
	const value = translator.translate(language, 'my_account', key);
	if (typeof value !== 'string' || !value || value === key || value.includes(key)) {
		return fallback;
	}
	return value;
};

const GoalReachedCard: React.FC<GoalReachedCardProps> = ({
	language,
	totalLostKg,
	currentKg,
	onSetNewGoal,
	weightUnit = 'kg',
}) => {
	const boldMessage = fillWeight(
		fillWeight(t(language, 'CareProfile/GoalReached/MessageBold', ' {{lostKg}} kg — now {{currentKg}} kg '), '{{lostKg}}', totalLostKg, weightUnit),
		'{{currentKg}}', currentKg, weightUnit,
	);

	return (
		<div
			id="goal-reached-card"
			className="flex flex-col w-full overflow-hidden rounded-[20px] border border-solid border-[rgba(255,185,9,0.4)] bg-[#faf4ef] relative"
		>
			<div className="absolute bg-[rgba(255,185,9,0.1)] blur-[43px] right-[-40px] top-[-70px] w-[138px] h-[138px] rounded-full pointer-events-none" />

			<div id="goal-reached-card-text" className="flex flex-col gap-y-2 p-7 pb-4 relative">
				<p className="bg-clip-text text-[28px] font-semibold leading-[32px] text-transparent" style={{ backgroundImage: titleGradient }}>
					{t(language, 'CareProfile/GoalReached/Title', 'Congratulations!')}
				</p>
				<p className="text-[18px] leading-[1.4] text-[#616975]">
					{t(language, 'CareProfile/GoalReached/MessageBefore', 'You lost')}
					<span className="bg-clip-text font-semibold text-transparent" style={{ backgroundImage: boldGradient }}>
						{boldMessage}
					</span>
					{t(language, 'CareProfile/GoalReached/MessageAfter', '. Keep going!')}
				</p>
			</div>

			<div id="goal-reached-card-illustration" className="relative h-[220px] w-full overflow-hidden flex items-start justify-center">
				<svg
					className="absolute inset-0 z-0 w-full h-full opacity-40"
					viewBox="0 0 400 220"
					preserveAspectRatio="none"
					aria-hidden="true"
				>
					<polyline points="20,50 100,75 180,90 260,125 340,160" fill="none" stroke="#e88103" strokeWidth="2" strokeDasharray="6 6" />
					<circle cx="20" cy="50" r="4" fill="#e88103" />
					<circle cx="100" cy="75" r="4" fill="#e88103" />
					<circle cx="180" cy="90" r="4" fill="#e88103" />
					<circle cx="260" cy="125" r="4" fill="#e88103" />
					<circle cx="340" cy="160" r="4" fill="#e88103" />
				</svg>
				<img
					id="goal-reached-card-image"
					src={careProfileAsset('plan-finished-illustration')}
					alt=""
					className="pointer-events-none relative z-10 h-[150%] w-auto max-w-none object-contain"
				/>
			</div>

			<div className="p-4 pt-0">
				<button
					id="goal-reached-card-cta"
					type="button"
					onClick={() => onSetNewGoal && onSetNewGoal()}
					className="w-full bg-care-plus-button-gradient hover:bg-care-plus-button-gradient-hover text-white font-medium py-3 px-6 transition-all duration-200 flex items-center justify-center gap-x-2 border-none rounded-full cursor-pointer"
				>
					{t(language, 'CareProfile/GoalReached/Cta', 'Set a new goal')}
					<FiChevronRight size={18} />
				</button>
			</div>
		</div>
	);
};

export default GoalReachedCard;
